import React from 'react';
import { C, CARD_W, DISRUPTOR_CARD_H, DISRUPTOR_CARD_GAP, getPathValue, resolveFeatureIconAndLabel } from '../flowCanvas.constants';
import LottieIcon from '../../common/LottieIcon';
import { getIconCategoryColor } from '../../common/iconCategory';

const FAILURE_RX = /(^|[\s_-])(fail(?:ed|ure)?|lost|out[\s_-]?of[\s_-]?moves)([\s_-]|$)/i;
const FAILURE_STROKE = '#dc2626';
const FAILURE_DRAG = '#f87171';
const FAILURE_CONTAINER_FILL = '#261214';
const FAILURE_CARD_FILL = '#34181c';
const FAILURE_CARD_STROKE = 'rgba(220,38,38,0.9)';
const FAILURE_EMPTY_FILL = 'rgba(220,38,38,0.08)';
const FAILURE_TEXT = '#fee2e2';

// ─── Disruptor trigger node — drag-and-drop slot for one or more disruptors ───
export default function DisruptorNode({
  step, stepX, stepY, isSelected, isDragOver,
  liveCustomisationData, campaignFeatures,
  onActivate, onSelectItem, onDragOver, onDragLeave, onDrop, onRemoveDisruptor,
}) {
  const W = CARD_W;

  const meta          = step.customisationConfig?.meta || {};
  const selectLink    = meta.disruptorSelectDataLink;
  const allowMultiple = meta.allowMultiple ?? step.customisationConfig?.variables?.[0]?.allowMultiple ?? true;
  const parts         = typeof selectLink === 'string' ? selectLink.split('.') : [];
  const valueKey      = parts.length ? parts[parts.length - 1] : null;
  const groupKeys     = parts.length ? parts.slice(0, -1) : [];

  const liveGroup = (liveCustomisationData && groupKeys.length)
    ? getPathValue(liveCustomisationData, groupKeys) : null;
  const allVals = Array.isArray(liveGroup)
    ? liveGroup
    : (step.populatedData?.variables?.[0]?.allValues || []);

  const failureCue = `${step.stepId || ''} ${step.stepTitle || ''} ${step.stepDescription || ''}`;
  const isFailureVariant = meta?.visualVariant === 'failure' || FAILURE_RX.test(failureCue);

  const items = (Array.isArray(allVals) ? allVals : [])
    .map((v, idx) => {
      const id = (v && typeof v === 'object' && valueKey) ? v[valueKey] : null;
      return { idx, id };
    })
    .filter(it => it.id !== null && it.id !== undefined && String(it.id).trim().length > 0);

  const displayCount = Math.max(items.length, 1);
  const totalH  = displayCount * (DISRUPTOR_CARD_H + DISRUPTOR_CARD_GAP) - DISRUPTOR_CARD_GAP;
  const topY    = stepY - totalH / 2;

  const outline    = isSelected ? C.sel : isDragOver ? (isFailureVariant ? FAILURE_DRAG : '#a78bfa') : (isFailureVariant ? FAILURE_STROKE : C.purple);
  const HEADER_H   = 46;
  const FOOTER_H   = 10;
  const CARD_PAD_X = 10;

  return (
    <g
      className="fc-node fc-node--disruptor"
      onClick={onActivate}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ cursor: 'pointer' }}
    >
      {/* Outer container */}
      <rect
        x={stepX - W / 2}
        y={topY - HEADER_H}
        width={W}
        height={totalH + HEADER_H + FOOTER_H}
        rx="10"
        fill={isFailureVariant ? FAILURE_CONTAINER_FILL : (isSelected ? C.cardSel : C.card)}
        stroke={outline}
        strokeWidth={isSelected ? 2 : 1.5}
      />
      {/* Header separator */}
      <line
        x1={stepX - W / 2 + 1} y1={topY - 2}
        x2={stepX + W / 2 - 1} y2={topY - 2}
        stroke={C.border} strokeWidth="1"
      />
      {/* Title */}
      <text
        x={stepX - W / 2 + 14} y={topY - 28}
        fontSize="11.5" fontWeight="700" fill={isFailureVariant ? FAILURE_TEXT : C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {step.stepTitle || 'Disruptor Trigger'}
      </text>
      {/* Hint */}
      <text
        x={stepX - W / 2 + 14} y={topY - 12}
        fontSize="8.5" fill={isFailureVariant ? '#fca5a5' : C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {allowMultiple ? 'Drag components here (multiple)' : 'Drag a component here'}
      </text>

      {/* Disruptor slot cards */}
      {(items.length ? items : [{ idx: -1, id: null }]).map((it, i) => {
        const baseY     = topY + i * (DISRUPTOR_CARD_H + DISRUPTOR_CARD_GAP);
        const cardX     = stepX - W / 2 + CARD_PAD_X;
        const cardY     = baseY + 2;
        const cardW     = W - CARD_PAD_X * 2;
        const cardH     = DISRUPTOR_CARD_H;
        const barH      = 42;
        const barY      = cardY + Math.max(0, (cardH - barH) / 2);
        const midY      = cardY + cardH / 2;
        const { label, icon, varKey, featureCategory } = resolveFeatureIconAndLabel(it.id, campaignFeatures);
        const displayLabel    = label.length > 22 ? label.slice(0, 22) + '…' : label;
        const isEmpty   = it.id === null;
        const canDelete = !!onRemoveDisruptor && it.idx !== -1;
        const categoryColor = getIconCategoryColor(featureCategory);
        const cardFill = isFailureVariant
          ? (isEmpty ? FAILURE_EMPTY_FILL : FAILURE_CARD_FILL)
          : (isEmpty ? C.circle : 'rgba(30, 38, 67, 0.98)');
        const cardStroke = isFailureVariant
          ? FAILURE_CARD_STROKE
          : (isEmpty ? C.border : 'rgba(255,255,255,0.08)');
        const handleSelectItem = (event) => {
          if (isEmpty || !onSelectItem) {
            return;
          }

          event.stopPropagation();
          onSelectItem({
            id: it.id,
            featureId: it.id,
            campaignFeatureId: it.id,
            label,
            parentStep: step.stepId || null,
            homeStepId: step.stepId || null,
          }, {
            source: 'expanded-flow-disruptor',
            clickedStepId: step.stepId || null,
            preserveTimelinePlayhead: true,
          });
        };

        return (
          <g key={`${step.stepId}-d-${it.idx}-${i}`}>
            {isEmpty ? (
              <>
                <rect x={cardX} y={barY} width={cardW} height={barH} rx="4"
                  fill={cardFill}
                  stroke={cardStroke}
                  strokeWidth="1"
                  style={{ cursor: 'default' }}
                />
                <text
                  x={cardX + cardW / 2}
                  y={midY + 5}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="400"
                  fill={isFailureVariant ? '#fca5a5' : C.textMuted}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {displayLabel}
                </text>
              </>
            ) : (
              <foreignObject
                x={cardX}
                y={barY}
                width={cardW}
                height={barH}
                style={{ overflow: 'visible' }}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  onClick={handleSelectItem}
                  style={{
                    height: `${barH}px`,
                    width: `${cardW}px`,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    position: 'relative',
                    gap: '0.35rem',
                    padding: '7px 9px',
                    background: isFailureVariant
                      ? FAILURE_CARD_FILL
                      : 'var(--workspace-panel-inner-item-bg-color, #4f5a73)',
                    border: `1px solid ${isFailureVariant ? '#f87171' : 'var(--workspace-panel-inner-item-bg-color, #3b465a)'}`,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    flexShrink: 0,
                    transition: 'background 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: onSelectItem ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        padding: '2px',
                        flexShrink: 0,
                        color: '#fff',
                        background: categoryColor,
                        boxSizing: 'border-box',
                      }}
                    >
                      <LottieIcon icon={icon} varKey={varKey} size={20} />
                    </span>
                    <span
                      style={{
                        fontWeight: 500,
                        color: isFailureVariant
                          ? FAILURE_TEXT
                          : 'var(--body-copy-color, #C4C4D6)',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        padding: 0,
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        lineHeight: 1.2,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        pointerEvents: 'none',
                      }}
                    >
                      {displayLabel}
                    </span>
                  </div>
                </div>
              </foreignObject>
            )}
            {/* Delete button */}
            {canDelete && (
              <g
                onClick={(e) => { e.stopPropagation(); onRemoveDisruptor(step, it.idx); }}
                style={{ cursor: 'pointer' }}
              >
                <circle cx={cardX + cardW - 14} cy={cardY + 14} r={10} fill="#dc2626" />
                <g transform={`translate(${cardX + cardW - 14}, ${cardY + 14})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                  <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                  <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                  <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                  <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                </g>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
}
